import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import notify from '../../utils/IANotification';
import { MdAdd, MdCheck, MdClose, MdEdit, MdRepeat, MdChevronLeft, MdChevronRight, MdToday, MdMeetingRoom, MdViewList, MdViewModule, MdUpload, MdCheckCircle, MdDownload } from 'react-icons/md';
import { fetch_all_bookings, approve_booking, reject_booking, fetch_all_locations, upload_bookings_csv } from '../../api/locationsAPI';
import FileUploadField from '../../components/FileUploadField';

export default function FacilityBookingsPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    useFetchUserData();

    const todayStr = new Date().toISOString().slice(0, 10);

    const [bookings, setBookings] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState("all");
    const [selectedDate, setSelectedDate] = useState(todayStr);
    const [filterByDate, setFilterByDate] = useState(true);
    const [selectedLocationId, setSelectedLocationId] = useState("all");
    const [viewMode, setViewMode] = useState("list"); // "list" | "room_grid"
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvText, setCsvText] = useState('');
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvResult, setCsvResult] = useState(null);

    const downloadSampleCsv = () => {
        const content = "Brief description,Area,Room,Start time,End time,Duration,Full Description,Type,Created by,Confirmation status,Last updated\nTuesday FUN 2026,Level 1,Fellowship Deck,10:30 - Tuesday 04 August 2026,13:00 - Tuesday 04 August 2026,2.5 hours,Booked for Christina Ong,Fellowship,Admin,Confirmed,2026-08-04";
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'sample_room_bookings.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileSelected = (files) => {
        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                setCsvText(e.target.result || '');
            };
            reader.readAsText(file);
        }
    };


    const loadData = async () => {
        setLoading(true);
        const params = statusFilter !== "all" ? { status: statusFilter } : {};
        const [resBookings, resLocs] = await Promise.all([
            fetch_all_bookings(params),
            fetch_all_locations(),
        ]);

        if (resBookings?.status === 200) {
            setBookings(resBookings.data || []);
        }
        if (resLocs?.status === 200) {
            setLocations(resLocs.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, [statusFilter]);

    const handleApprove = async (booking_id) => {
        const res = await approve_booking(booking_id);
        if (res?.status === 200) {
            notify({ type: "success", message: "Booking approved successfully" });
            loadData();
        } else {
            notify({ type: "error", message: res?.message || "Failed to approve booking" });
        }
    };

    const handleReject = async (booking_id) => {
        const reason = window.prompt("Enter rejection reason (optional):");
        if (reason === null) return;
        const res = await reject_booking(booking_id, reason);
        if (res?.status === 200) {
            notify({ type: "success", message: "Booking rejected" });
            loadData();
        } else {
            notify({ type: "error", message: res?.message || "Failed to reject booking" });
        }
    };

    const changeDateByDays = (days) => {
        const d = new Date(selectedDate);
        d.setDate(d.getDate() + days);
        setSelectedDate(d.toISOString().slice(0, 10));
        setFilterByDate(true);
    };

    const handleCsvUpload = async () => {
        if (!csvText.trim()) return;
        setCsvUploading(true);
        setCsvResult(null);
        const res = await upload_bookings_csv(csvText);
        setCsvUploading(false);
        if (res?.status === 200) {
            setCsvResult({ success: true, message: res.message });
            loadData();
            setTimeout(() => {
                setShowCsvModal(false);
                setCsvText('');
                setCsvResult(null);
            }, 1500);
        } else {
            setCsvResult({ success: false, message: res?.message || 'Upload failed' });
        }
    };

    // Filter bookings based on selected location and date
    const filteredBookings = bookings.filter((b) => {
        if (selectedLocationId !== "all" && String(b.location_id) !== String(selectedLocationId)) {
            return false;
        }
        if (filterByDate && selectedDate) {
            const start = new Date(b.start_datetime);
            const end = new Date(b.end_datetime);
            const targetStart = new Date(`${selectedDate}T00:00:00`);
            const targetEnd = new Date(`${selectedDate}T23:59:59.999`);
            if (end < targetStart || start > targetEnd) {
                return false;
            }
        }
        return true;
    });

    const columns = [
        {
            header: "Booking Title & Purpose",
            accessor: "title",
            modifier: (val, row) => (
                <div>
                    <span className="font-bold text-gray-900">{row.title}</span>
                    <p className="text-xs text-gray-500">{row.location_data?.name || `Location #${row.location_id}`}</p>
                </div>
            )
        },
        {
            header: "Applicant",
            accessor: "user_data",
            modifier: (val, row) => (
                <span className="text-xs font-semibold text-gray-800">
                    {row.user_data ? `${row.user_data.first_name} ${row.user_data.last_name}` : `User #${row.user_id}`}
                </span>
            )
        },
        {
            header: "Start Time",
            accessor: "start_datetime",
            modifier: (val) => new Date(val).toLocaleString()
        },
        {
            header: "End Time",
            accessor: "end_datetime",
            modifier: (val) => new Date(val).toLocaleString()
        },
        {
            header: "Recurrence",
            accessor: "is_recurring",
            modifier: (val, row) => (
                row.is_recurring === "1" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        <MdRepeat className="text-xs" />
                        {row.recurrence_rule === "weekly_count" ? `Weekly (${row.recurrence_count}x)` : "Weekly"}
                    </span>
                ) : (
                    <span className="text-xs text-gray-500">One-off</span>
                )
            )
        },
        {
            header: "Status",
            accessor: "status",
            modifier: (val) => {
                const colors = {
                    approved: "bg-emerald-100 text-emerald-800",
                    pending: "bg-amber-100 text-amber-800",
                    rejected: "bg-rose-100 text-rose-800",
                    cancelled: "bg-gray-100 text-gray-800",
                };
                return (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${colors[val] || "bg-gray-100 text-gray-800"}`}>
                        {val}
                    </span>
                );
            }
        },
    ];

    return (
        <AdminLayout
            navSelected={"Facility Bookings"}
            headerText={"Facility Bookings"}
            toolbar={
                <>
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-gray-900">Facility Bookings</h1>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" icon={MdUpload} onClick={() => { setShowCsvModal(true); setCsvResult(null); setCsvText(''); }}>
                            Import CSV
                        </Button>
                        <Button variant="primary" icon={MdAdd} onClick={() => navigate('/admin/locations/bookings/details?booking_id=new')}>
                            New Booking
                        </Button>
                    </div>
                </>
            }
        >
            {/* FILTER & SCHEDULE CONTROL BAR */}
            <div className="mb-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-white rounded-2xl border border-gray-200 shadow-sm">
                    
                    {/* SPECIFIED DAY PICKER & QUICK NAV */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Date Schedule:</span>
                        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl border border-gray-200">
                            <button
                                type="button"
                                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition"
                                title="Previous Day"
                                onClick={() => changeDateByDays(-1)}
                            >
                                <MdChevronLeft className="text-lg" />
                            </button>

                            <input
                                type="date"
                                className="text-xs font-semibold bg-transparent border-0 focus:outline-none text-gray-900 px-2 cursor-pointer"
                                value={selectedDate}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    setFilterByDate(true);
                                }}
                            />

                            <button
                                type="button"
                                className="p-1 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition"
                                title="Next Day"
                                onClick={() => changeDateByDays(1)}
                            >
                                <MdChevronRight className="text-lg" />
                            </button>
                        </div>

                        <Button
                            variant={filterByDate && selectedDate === todayStr ? "primary" : "secondary"}
                            size="sm"
                            icon={MdToday}
                            onClick={() => {
                                setSelectedDate(todayStr);
                                setFilterByDate(true);
                            }}
                        >
                            Today
                        </Button>

                        {filterByDate ? (
                            <button
                                type="button"
                                className="text-xs text-blue-600 hover:underline font-semibold ml-1"
                                onClick={() => setFilterByDate(false)}
                            >
                                Clear Date Filter (All Dates)
                            </button>
                        ) : (
                            <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200">
                                Showing All Dates
                            </span>
                        )}
                    </div>

                    {/* LOCATION ROOM SELECTOR & VIEW MODE TOGGLE */}
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                            <MdMeetingRoom className="text-gray-500 text-base" />
                            <select
                                className="select-input !mb-0 text-xs py-1.5 font-medium"
                                value={selectedLocationId}
                                onChange={(e) => setSelectedLocationId(e.target.value)}
                            >
                                <option value="all">All Location Rooms</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name} ({loc.location_type})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center p-0.5 bg-gray-100 rounded-xl border border-gray-200">
                            <button
                                type="button"
                                className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold transition ${viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
                                onClick={() => setViewMode("list")}
                            >
                                <MdViewList /> Table View
                            </button>
                            <button
                                type="button"
                                className={`flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold transition ${viewMode === "room_grid" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
                                onClick={() => setViewMode("room_grid")}
                            >
                                <MdViewModule /> By Room Schedule
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-center text-gray-500 font-medium">Loading bookings...</div>
            ) : viewMode === "list" ? (
                <DataTable
                    columns={columns}
                    data={filteredBookings}
                    enableActions
                    actions={(row) => (
                        <div className="flex items-center gap-2">
                            {row.status === "pending" && (
                                <>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        icon={MdCheck}
                                        onClick={() => handleApprove(row.id)}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        icon={MdClose}
                                        onClick={() => handleReject(row.id)}
                                    >
                                        Reject
                                    </Button>
                                </>
                            )}
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdEdit}
                                onClick={() => navigate(`/admin/locations/bookings/details?booking_id=${row.id}`)}
                            >
                                Details
                            </Button>
                        </div>
                    )}
                />
            ) : (
                /* BY ROOM SCHEDULE GRID VIEW */
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {(selectedLocationId === "all" ? locations : locations.filter(l => String(l.id) === String(selectedLocationId))).map((loc) => {
                        const roomBookings = filteredBookings.filter(b => String(b.location_id) === String(loc.id));
                        return (
                            <Card
                                key={loc.id}
                                title={loc.name}
                                subtitle={`${loc.location_type} • Capacity: ${loc.capacity} pax`}
                            >
                                <div className="space-y-3">
                                    {roomBookings.length > 0 ? (
                                        roomBookings.map((b) => (
                                            <div key={b.id} className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-1.5">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-bold text-xs text-gray-900">{b.title}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                                                        b.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                                                        b.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                                                    }`}>
                                                        {b.status}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-600 font-medium">
                                                    {new Date(b.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(b.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-500 pt-1 border-t border-gray-100">
                                                    <span>Applicant: {b.user_data ? `${b.user_data.first_name} ${b.user_data.last_name}` : `#${b.user_id}`}</span>
                                                    <button
                                                        type="button"
                                                        className="text-blue-600 hover:underline font-semibold"
                                                        onClick={() => navigate(`/admin/locations/bookings/details?booking_id=${b.id}`)}
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-6 text-center text-xs text-gray-400 font-medium bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                                            No bookings scheduled for this room on {filterByDate ? selectedDate : "selected filters"}.
                                        </div>
                                    )}

                                    <div className="pt-2 flex justify-end">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            icon={MdAdd}
                                            onClick={() => navigate(`/admin/locations/bookings/details?booking_id=new&location_id=${loc.id}`)}
                                        >
                                            Book This Room
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* CSV Upload Modal */}
            {showCsvModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative border border-gray-200 space-y-4">
                        <button
                            onClick={() => setShowCsvModal(false)}
                            className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                        >
                            <MdClose size={22} />
                        </button>

                        <div className="flex items-center justify-between pr-8">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Import Room Bookings via CSV</h3>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    Upload a CSV file or paste content below. Expected headers:
                                </p>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                icon={MdDownload}
                                onClick={downloadSampleCsv}
                            >
                                Download Sample CSV
                            </Button>
                        </div>

                        <p className="text-[11px] font-mono bg-gray-100 rounded p-2 text-gray-700">
                            Brief description, Area, Room, Start time, End time, Duration, Full Description, Type, Created by, Confirmation status, Last updated
                        </p>

                        {/* File Upload Component */}
                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <FileUploadField
                                item_index="bookings_csv"
                                max_file_size={5}
                                max_files={1}
                                text="Select or Drag Bookings CSV File"
                                subtext="Supported format: .csv"
                                onChange={(files) => handleFileSelected(files)}
                            />
                        </div>

                        {/* Text Preview / Edit Area */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                                CSV Content Preview / Manual Edit:
                            </label>
                            <textarea
                                rows={6}
                                value={csvText}
                                onChange={(e) => setCsvText(e.target.value)}
                                placeholder={`Brief description,Area,Room,Start time,End time,Duration,Full Description,Type,Created by,Confirmation status\nTuesday FUN 2026,Level 1,Fellowship Deck,10:30 - Tuesday 04 August 2026,13:00 - Tuesday 04 August 2026,2.5 hours,Booked for Christina Ong,,Admin,Confirmed`}
                                className="w-full border border-gray-200 rounded-xl p-3 text-xs font-mono bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {csvResult && (
                            <div className={`p-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
                                csvResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}>
                                <MdCheckCircle size={18} />
                                {csvResult.message}
                            </div>
                        )}

                        <div className="flex gap-3 pt-2">
                            <Button variant="outline" className="flex-1" icon={MdClose} onClick={() => setShowCsvModal(false)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="flex-1"
                                icon={MdUpload}
                                onClick={handleCsvUpload}
                                disabled={csvUploading || !csvText.trim()}
                            >
                                {csvUploading ? 'Uploading...' : 'Process & Upload Bookings'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

        </AdminLayout>

    );
}
